import { LoadingPage } from '@act/storybook';
import { Typography } from '@mui/material';
import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ReportPrintToolbar } from '../components/ReportPrintToolbar';
import { useReportReadingPageData } from '../hooks/useReportReadingPageData.hook';
import { useReportPrintSectionFit } from '../hooks/useReportPrintSectionFit.hook';
import ReportReadingColumn from '../preview/ReportReadingColumn';

const REPORTS_PRINT_BODY_CLASS = 'reports-print-active';
const REPORTS_PRINT_HTML_CLASS = 'reports-print-active-html';

/**
 * Chromeless print view: renders live report sections and triggers print.
 */
const ReportPrintPage = () => {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const onlySectionId = searchParams.get('section')?.trim() || '';

  const {
    report,
    customer,
    reportLoading,
    reportError,
    readingItems,
    toolbarSections,
    cadence,
    routeHeader,
    measurementPeriod,
    isReady,
  } = useReportReadingPageData(reportId, onlySectionId);

  useEffect(() => {
    document.documentElement.classList.add(REPORTS_PRINT_HTML_CLASS);
    document.body.classList.add(REPORTS_PRINT_BODY_CLASS);
    return () => {
      document.documentElement.classList.remove(REPORTS_PRINT_HTML_CLASS);
      document.body.classList.remove(REPORTS_PRINT_BODY_CLASS);
    };
  }, []);

  useReportPrintSectionFit(isReady);

  if (!reportLoading && !report && reportError) {
    return (
      <div className="reports-print-page">
        <Typography variant="h6">Report not found</Typography>
        <Link to="/">Back to reports</Link>
      </div>
    );
  }

  if (!isReady || !report || !customer || !routeHeader) {
    return <LoadingPage />;
  }

  return (
    <div className="reports-print-page">
      <ReportPrintToolbar
        title={routeHeader.title}
        subtitle={routeHeader.subtitle}
        reportId={reportId}
        sections={toolbarSections}
        activeSectionId={onlySectionId}
      />

      <div className="reports-print-document-stage">
        <div className="reports-print-document-window">
          <ReportReadingColumn
            customer={customer}
            reportingPeriod={report.reportingPeriod}
            measurementPeriod={measurementPeriod}
            cadence={cadence}
            items={readingItems}
            mode="preview"
            reportId={reportId}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportPrintPage;
